import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_ACCENT, isAdmin } from "@prism/shared/constants";
import { apiFetch } from "../lib/api.js";
import { applyTheme } from "../lib/theme.js";
import { useAuth } from "../context/AuthContext.jsx";

/**
 * useActiveClient
 *
 * Resolves the *effective* client the UI should display — either the
 * authenticated user's home client (default) or another client the admin
 * has switched to via ?clientId=<id>.
 *
 * When viewing a non-home client, fetches its full profile (name, accent,
 * dataSources) from GET /api/clients/:id so the Sidebar + theme + module
 * registry all reflect the viewed client. When switching back to home,
 * the auth-provided home client is reused instantly.
 *
 * Side effect: applies the viewed client's accentColor to the CSS vars so
 * the whole UI rebrands on switch. Reverts to home accent when switching
 * back.
 *
 * Returns:
 *   client        — the client to display (always defined once loaded)
 *   isViewingAs   — boolean, true when viewing a non-home client
 *   loading       — true during the fetch for a non-home client
 *   error         — error from the fetch, if any
 */
export function useActiveClient() {
  const { user, client: homeClient } = useAuth();
  const [searchParams] = useSearchParams();

  const homeClientId = homeClient?.id || user?.clientId;
  const requestedId = searchParams.get("clientId") || homeClientId;
  const isHome = requestedId === homeClientId;
  // Admins and EMPLOYEEs can view another client. Admins use the admin endpoint;
  // EMPLOYEEs use the scope-checked /api/me endpoint (the admin one would 403).
  const isEmployee = user?.role === "EMPLOYEE";
  const canView = isAdmin(user?.role) || isEmployee;

  // If non-admins try ?clientId=other, silently ignore (backend does the same).
  const targetId = !isHome && !canView ? homeClientId : requestedId;
  const viewingAs = !isHome && canView;

  const [viewed, setViewed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Home client: nothing to fetch.
    if (!viewingAs) {
      setViewed(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const url = isEmployee ? `/api/me/clients/${targetId}` : `/api/clients/${targetId}`;
    apiFetch(url)
      .then((data) => { if (!cancelled) setViewed(data); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [viewingAs, targetId, isEmployee]);

  // Apply the effective client's accent to the theme. Reverts to default
  // if both viewed and home are unavailable (shouldn't happen in practice).
  const effective = viewingAs ? viewed : homeClient;
  useEffect(() => {
    applyTheme(effective?.accentColor || DEFAULT_ACCENT);
  }, [effective?.accentColor]);

  return {
    client: effective,
    isViewingAs: viewingAs,
    loading,
    error,
  };
}
